import { createQueryNormalizer } from '@normy/react-query';
import {
    type DataTag,
    type DefaultError,
    type QueryClient,
    type QueryFunction,
    type QueryKey,
    type SkipToken,
    skipToken,
    type UseQueryOptions,
} from '@tanstack/react-query';
import {
    ENTITY_REF_FIELDS,
    type EntityKind,
    type EntityKinds,
    type Normalized,
} from './entityKinds';
import { type AnyMemberField, type AnyMemberKey } from './normalizedTypes';
import {
    defineField,
    entityKey,
    getEntityKey,
    KIND_KEY,
    toNormalized,
} from './toNormalized';

type QueryNormalizer = ReturnType<typeof createQueryNormalizer>;
type NormalizerData = Parameters<QueryNormalizer['setNormalizedData']>[0];

/** The value of an `EntityUpdate` field holding another entity: that entity's `Id`. */
export type EntityLink = Readonly<{ Id: string }>;

/** The fields of `Kind` that hold another entity (`ENTITY_REF_FIELDS`). */
type RefField<Kind extends EntityKind> = Kind extends keyof typeof ENTITY_REF_FIELDS
    ? keyof (typeof ENTITY_REF_FIELDS)[Kind]
    : never;

/**
 * Fields of any of the entity's shapes (members of `T`). Fields holding another
 * entity take an `EntityLink` to it.
 */
type EntityFields<T, RefFields extends PropertyKey> = {
    [K in AnyMemberKey<T>]?: K extends RefFields
        ? EntityLink | Extract<AnyMemberField<T, K>, undefined>
        : AnyMemberField<T, K>;
};

/** New values for some of an entity's fields; omitted fields keep theirs. */
export type EntityUpdate = {
    [Kind in EntityKind]: Readonly<{
        kind: Kind;
        id: string;
        /**
         * Nested objects are merged into the entity's; arrays and other values
         * replace its own. An `undefined` field replaces the entity's value, so
         * omit fields to leave them unchanged.
         *
         * Fields holding another entity (e.g. an Alert's `Issue`) take its `Id`, as
         * `{ Id }`: the field then holds that entity as normalized queries hold it,
         * and follows its later updates. Only the `Id` is used, so a whole entity
         * can be passed without updating it.
         */
        fields: EntityFields<EntityKinds[Kind], RefField<Kind>>;
    }>;
}[EntityKind];

/** Mutation context that renders entity updates while the mutation runs. */
// an interface rather than a type alias, so that types inferred from it (e.g.
// a mutation hook's) can name it rather than normy's internal types
export interface OptimisticEntitiesContext {
    readonly optimisticData: NormalizerData;
}

export type NormalizedQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryKey extends QueryKey,
> = Omit<
    UseQueryOptions<Normalized<TQueryFnData>, TError, TData, TQueryKey>,
    'queryFn' | 'structuralSharing'
> & {
    /** returns SDK data, as the SDK client does */
    queryFn: QueryFunction<TQueryFnData, TQueryKey> | SkipToken;
};

/**
 * A cache of the entities (e.g. Issues) held by queries in `queryClient`,
 * keeping every normalized query's copy of an entity the same. See
 * `shared/normalized_cache` for usage.
 */
export function createNormalizedCache(queryClient: QueryClient) {
    // Queries opt in, so that existing queries are unaffected
    const normalizer = createQueryNormalizer(queryClient, {
        normalize: false,
        getNormalizationObjectKey: getEntityKey,
    });
    normalizer.subscribe();

    /**
     * Query options for a query whose data is normalized: its SDK data is
     * converted with `toNormalized`, and the entities in it are shared with
     * every other normalized query. Each fetch updates those entities wherever
     * they are held, and changes to them from elsewhere update this query's
     * data.
     *
     * Use it like `queryOptions` -- e.g. with `useQuery` or `fetchQuery`.
     * Only a query created with these options is normalized; don't
     * `setQueryData` its key before then.
     */
    function normalizedQueryOptions<
        TQueryFnData,
        TError = DefaultError,
        TData = Normalized<TQueryFnData>,
        TQueryKey extends QueryKey = QueryKey,
    >(options: NormalizedQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
        const { queryFn } = options;
        const normalizedOptions: UseQueryOptions<
            Normalized<TQueryFnData>,
            TError,
            TData,
            TQueryKey
        > = {
            ...options,
            meta: { ...options.meta, normalize: true },
            // Dates are compared by value, so that unchanged entities keep
            // their reference across refetches
            structuralSharing: shareEqualData,
            queryFn:
                queryFn === skipToken
                    ? skipToken
                    : async (context) => {
                          const data = toNormalized<TQueryFnData>(await queryFn(context));
                          // TanStack discards a cancelled fetch's result, but
                          // `queryFn` may have ignored the signal, so its data
                          // must not reach other queries either. (Reading the
                          // signal only after the fetch leaves TanStack's
                          // cancel-on-unmount behavior to `queryFn`.)
                          context.signal.throwIfAborted();
                          // The normalizer only updates other queries on
                          // writes, not on fetches
                          normalizer.setNormalizedData(data as NormalizerData);
                          return data;
                      },
        };
        // tagged like `queryOptions` does, so that the key types query data
        return normalizedOptions as typeof normalizedOptions & {
            queryKey: DataTag<TQueryKey, Normalized<TQueryFnData>, TError>;
        };
    }

    /**
     * The entity's fields held by normalized queries, or `undefined` when none
     * holds it. Only fields that some query holds are present: e.g. an Issue
     * held only as an Alert's embedded Issue has just those fields.
     */
    function getEntity<Kind extends EntityKind>(
        kind: Kind,
        id: string,
    ): Partial<EntityKinds[Kind]> | undefined {
        return normalizer.getObjectById(entityKey(kind, id)) as
            | Partial<EntityKinds[Kind]>
            | undefined;
    }

    /**
     * Updates entities wherever normalized queries hold them, e.g. from a
     * real-time event. Entities that no query holds are ignored. Doesn't
     * change the queries' staleness.
     */
    function updateEntities(updates: readonly EntityUpdate[]) {
        normalizer.setNormalizedData(toEntityData(updates));
    }

    return {
        normalizedQueryOptions,
        getEntity,
        updateEntities,
    };
}

/**
 * Mutation context, to return from `onMutate`, that renders `updates`
 * while the mutation runs, and reverts them if it fails. A successful
 * mutation keeps them until the entities are next fetched -- including by
 * a fetch that started before the mutation finished, which may revert
 * them.
 *
 * Applies only to entities that normalized queries hold when the mutation
 * starts.
 */
export function optimisticEntities(
    updates: readonly EntityUpdate[],
): OptimisticEntitiesContext {
    // Keyed by index rather than an array -- `{ 0: updateA, 1: updateB }`, not
    // `[updateA, updateB]`. normy snapshots the fields to revert per path, and
    // all items of an array share one path, so reverting would only restore
    // the fields of the last update.
    return { optimisticData: Object.fromEntries(toEntityData(updates).entries()) };
}

const refFieldsByKind: Readonly<
    Partial<Record<EntityKind, Readonly<Partial<Record<string, EntityKind>>>>>
> = ENTITY_REF_FIELDS;

function toEntityData(updates: readonly EntityUpdate[]) {
    return updates.map(({ kind, id, fields }) => {
        const refFields = refFieldsByKind[kind] ?? {};
        return {
            ...Object.fromEntries(
                Object.entries(fields).map(([key, value]) => {
                    const refKind = refFields[key];
                    return [
                        key,

                        refKind === undefined || value === undefined
                            ? value
                            : toLinkData(refKind, value),
                    ];
                }),
            ),
            Id: id,
            [KIND_KEY]: kind,
        };
    });
}

/**
 * `link` marked as entities are in `toNormalized`, so that the normalizer
 * stores a reference to the entity. Its other fields are dropped.
 */
function toLinkData(kind: EntityKind, link: unknown) {
    if (
        typeof link !== 'object' ||
        link === null ||
        !('Id' in link) ||
        typeof link.Id !== 'string'
    ) {
        throw new Error(`A link to a ${kind} must have a string Id`);
    }
    return { Id: link.Id, [KIND_KEY]: kind };
}

/**
 * `next`, reusing every part of `prev` that is equal to it, so that unchanged
 * data keeps its reference. Like TanStack's default structural sharing, but
 * comparing `Date`s by value.
 */
export function shareEqualData(prev: unknown, next: unknown): unknown {
    if (prev === next) {
        return prev;
    }
    if (prev instanceof Date && next instanceof Date) {
        return prev.getTime() === next.getTime() ? prev : next;
    }
    if (Array.isArray(prev) && Array.isArray(next)) {
        const shared = next.map((item, i) => shareEqualData(prev[i], item));
        return shared.length === prev.length &&
            shared.every((item, i) => item === prev[i])
            ? prev
            : shared;
    }
    if (isPlainObject(prev) && isPlainObject(next)) {
        const prevKeys = Object.keys(prev);
        const shared: Record<string, unknown> = {};
        let isEqual = prevKeys.length === Object.keys(next).length;
        for (const [key, value] of Object.entries(next)) {
            const sharedValue = shareEqualData(prev[key], value);
            defineField(shared, key, sharedValue);
            if (sharedValue !== prev[key] || !Object.hasOwn(prev, key)) {
                isEqual = false;
            }
        }
        return isEqual ? prev : shared;
    }
    return next;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const prototype: unknown = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
